import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  Clock,
  MessageSquare,
  PhoneIncoming,
  PhoneOutgoing,
  Search,
  Timer,
  Zap,
} from "lucide-react";
import { PageHeader } from "../components/layout/PageHeader";
import { useLivePolling } from "../hooks/useLivePolling";
import { useSession } from "../hooks/useSession";
import { customerDashboardApi } from "../api/customerDashboardApi";
import type { LiveCallsResponse } from "../api/customerDashboardApi";
import { ScrollShadow } from "../components/ui/ScrollShadow";
import { fmtTime, tzLabel } from "../components/reports/ReportBits";

const LIVE_POLL_MS = 3000;

/**
 * Detail report for the "Current sessions" dashboard tile. Shows
 * every column `spCustomerDashboard_LiveCallsList` returns (Time,
 * DNIS, Description, Duration, State, Info) plus inbound / outbound
 * / chat counters, with a fast (3s) live refresh and a client-side
 * search box for big customers. Rows are sorted oldest-first so the
 * sessions that have been running the longest sit at the top of the
 * report — that's where the operational attention usually goes.
 */
export function LiveCallsReportPage() {
  const { session } = useSession();
  const [search, setSearch] = useState("");
  const { data, error, loading } = useLivePolling<LiveCallsResponse>({
    fetcher: () => customerDashboardApi.liveCalls(),
    intervalMs: LIVE_POLL_MS,
  });

  const showChat = useMemo(
    () => new Set(session?.permissions ?? []).has("CUSTOMER.DASHBOARD.WIDGETS.CHAT"),
    [session?.permissions],
  );

  const all = data?.calls ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? all.filter(
          (c) =>
            (c.dnis ?? "").toLowerCase().includes(q) ||
            (c.description ?? "").toLowerCase().includes(q) ||
            (c.info ?? "").toLowerCase().includes(q) ||
            (c.state ?? "").toLowerCase().includes(q),
        )
      : all;

    // Oldest first: the calls that have been running the longest
    // (largest duration / earliest start) move to the top of the
    // report so they read as the most "interesting" rows. Falling
    // back to duration keeps a stable order if two sessions started
    // in the same second.
    return [...base].sort((a, b) => {
      const ta = Date.parse(a.timeUtc || "");
      const tb = Date.parse(b.timeUtc || "");
      const aValid = !isNaN(ta);
      const bValid = !isNaN(tb);
      if (aValid && bValid && ta !== tb) return ta - tb;
      return (b.duration || 0) - (a.duration || 0);
    });
  }, [all, search]);

  return (
    <>
      <PageHeader
        title="Current sessions"
        actions={
          <Link
            to="/dashboard"
            aria-label="Back to dashboard"
            className="btn btn-secondary" style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none" }}
          >
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
        }
      />
      <div className="dashboard">
        <section className="tile" aria-label="Current sessions report">
          <header className="tile-head" style={{ gap: 12, flexWrap: "wrap", margin: 0 }}>
            <h3 style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  background: "var(--portal-primary-soft)",
                  color: "var(--portal-primary-darker)",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <Activity size={18} />
              </span>
              Current sessions
              <span
                style={{
                  marginLeft: 4,
                  padding: "2px 10px",
                  borderRadius: 999,
                  background: "var(--portal-primary-soft)",
                  color: "var(--portal-primary-darker)",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                }}
              >
                {data?.total ?? 0}
              </span>
            </h3>

            <div
              style={{
                display: "flex",
                gap: 14,
                alignItems: "center",
                color: "var(--portal-muted)",
                fontSize: "0.85rem",
                flexWrap: "wrap",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <PhoneIncoming size={14} /> Inbound{" "}
                <strong style={{ color: "var(--portal-text)" }}>{data?.callsInActive ?? 0}</strong>
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <PhoneOutgoing size={14} /> Outbound{" "}
                <strong style={{ color: "var(--portal-text)" }}>{data?.callsOutActive ?? 0}</strong>
              </span>
              {showChat && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <MessageSquare size={14} /> Chat{" "}
                  <strong style={{ color: "var(--portal-text)" }}>{data?.chatsActive ?? 0}</strong>
                </span>
              )}
            </div>

            <div
              style={{
                marginLeft: "auto",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                border: "1px solid var(--portal-border)",
                borderRadius: 10,
                color: "var(--portal-muted)",
                fontSize: "0.85rem",
                background: "white",
              }}
            >
              <Search size={14} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by number, description, agent…"
                autoFocus
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  color: "var(--portal-text)",
                  fontSize: "0.9rem",
                  minWidth: 240,
                }}
              />
            </div>
          </header>

          {loading && !data && (
            <div style={{ color: "var(--portal-muted)", padding: "20px 0" }}>Loading current sessions…</div>
          )}
          {Boolean(error) && !data && (
            <div style={{ color: "#b91c1c", padding: "20px 0" }}>Could not load current sessions.</div>
          )}

          {filtered.length === 0 && data && (
            <div style={{ color: "var(--portal-muted)", padding: "20px 0", textAlign: "center" }}>
              {search ? "No sessions match your filter." : "No active sessions right now."}
            </div>
          )}

          {filtered.length > 0 && (
            <ScrollShadow>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  fontSize: "0.92rem",
                }}
              >
                <thead>
                  <tr style={{ textAlign: "left", color: "var(--portal-muted)" }}>
                    <Th>Time ({tzLabel()})</Th>
                    <Th>State</Th>
                    <Th>DNIS</Th>
                    <Th>Description</Th>
                    <Th align="right">Duration</Th>
                    <Th>Info</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={`${c.cdrId}-${c.callId}`} style={{ borderTop: "1px solid var(--portal-border)" }}>
                      <Td mono>{fmtTime(c.timeUtc)}</Td>
                      <Td><CallStateBadge state={c.state} /></Td>
                      <Td mono>{c.dnis || "—"}</Td>
                      <Td>{c.description || "—"}</Td>
                      <Td align="right" mono>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <Clock size={14} aria-hidden="true" />
                          {formatHHMMSS(c.duration)}
                        </span>
                      </Td>
                      <Td>{c.info || "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollShadow>
          )}
        </section>
      </div>
    </>
  );
}

function CallStateBadge({ state }: { state: string }) {
  const upper = (state || "").toUpperCase();
  const palette: Record<string, { bg: string; fg: string; icon: React.ReactNode }> = {
    CONNECTED: { bg: "rgba(34,197,94,0.15)", fg: "#15803d", icon: <PhoneOutgoing size={12} /> },
    RINGING: { bg: "rgba(59,130,246,0.15)", fg: "#1d4ed8", icon: <PhoneIncoming size={12} /> },
    QUEUE: { bg: "rgba(245,158,11,0.18)", fg: "#a16207", icon: <Timer size={12} /> },
    IVR: { bg: "rgba(99,102,241,0.15)", fg: "#4338ca", icon: <Zap size={12} /> },
    "EARLY MEDIA": { bg: "rgba(99,102,241,0.15)", fg: "#4338ca", icon: <Zap size={12} /> },
    IN_CHAT: { bg: "rgba(217,70,239,0.15)", fg: "#86198f", icon: <MessageSquare size={12} /> },
  };
  const p =
    palette[upper] ?? {
      bg: "var(--portal-primary-soft)",
      fg: "var(--portal-primary-darker)",
      icon: <Zap size={12} />,
    };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 999,
        background: p.bg,
        color: p.fg,
        fontSize: "0.7rem",
        fontWeight: 700,
        letterSpacing: "0.02em",
        textTransform: "uppercase",
      }}
    >
      {p.icon} {upper || "—"}
    </span>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: "left" | "right" }) {
  return (
    <th
      style={{
        textAlign: align ?? "left",
        padding: "10px 8px",
        fontSize: "0.78rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  mono,
}: {
  children: React.ReactNode;
  align?: "left" | "right";
  mono?: boolean;
}) {
  return (
    <td
      style={{
        padding: "10px 8px",
        textAlign: align ?? "left",
        fontFamily: mono ? "var(--portal-font-mono, monospace)" : undefined,
        verticalAlign: "middle",
      }}
    >
      {children}
    </td>
  );
}

function formatHHMMSS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
}


